import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface Row {
  submission_id: string;
  lead_id: string;
  product_slug: string;
  product_name: string;
  product_group: string;
  submitted_at: string;
  lead_created_at: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  answers: Record<string, any>;
}

function renderAnswers(ans: Record<string, any>) {
  if (!ans || typeof ans !== 'object') return <span className="text-gray-500">N/A</span>;

  const valueFor = (...keys: string[]) => {
    for (const k of keys) {
      const v = (ans as any)[k];
      if (v !== undefined && v !== null && String(v).trim?.() !== '') return v;
    }
    return undefined;
  };

  const toNumber = (v: any) => {
    const n = Number(String(v).replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : null;
  };
  const fmtInt = (v: any) => {
    const n = toNumber(v);
    return n === null ? String(v) : new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);
  };
  const fmtAmount = (v: any) => {
    const n = toNumber(v);
    return n === null ? String(v) : new Intl.NumberFormat('en-US').format(n);
  };

  const ownOrLease = valueFor('ownOrLease', 'ownerOnLease');
  const sumInsured = valueFor('sumInsured', 'sum_insured');
  const propertyLocation = valueFor('propertyLocation', 'property_location', 'location');
  const numberOfEmployees = valueFor('numberOfEmployees', 'employees');
  const budgetPerEmployee = valueFor('budgetPerEmployee', 'budget_per_employee');
  const comments = valueFor('comments', 'notes');
  const tradeLicence = valueFor('tradeLicense', 'tradeLicence');

  const bullets: string[] = [];
  if (ownOrLease) {
    const s = String(ownOrLease).toLowerCase();
    if (s.startsWith('o')) bullets.push('Customer owns the property.');
    else if (s.startsWith('l')) bullets.push('Customer leases the property.');
    else bullets.push(`Ownership: ${String(ownOrLease)}.`);
  }
  if (propertyLocation) bullets.push(`Property location: ${String(propertyLocation)}.`);
  if (sumInsured) bullets.push(`Desired sum insured: ${fmtAmount(sumInsured)}.`);
  if (numberOfEmployees) bullets.push(`They have around ${fmtInt(numberOfEmployees)} employees.`);
  if (budgetPerEmployee) bullets.push(`Budget per employee: ${fmtAmount(budgetPerEmployee)}.`);
  if (tradeLicence) bullets.push(`Trade licence: ${String(tradeLicence)}.`);
  if (comments) bullets.push(`Notes: ${String(comments)}`);

  // Add any other fields we didn't cover explicitly as Label: value
  const used = new Set([
    'ownOrLease','ownerOnLease','sumInsured','sum_insured','propertyLocation','property_location','location',
    'numberOfEmployees','employees','budgetPerEmployee','budget_per_employee','comments','notes','tradeLicense','tradeLicence',
    'leadId','id','name','email','phone','contact_name','contact_email','contact_phone'
  ]);
  const titleCase = (s: string) => s
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  Object.entries(ans).forEach(([k, v]) => {
    if (used.has(k)) return;
    if (k.toLowerCase().endsWith('id')) return;
    if (v === null || v === undefined) return;
    const isEmptyObj = typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0;
    if (isEmptyObj) return;
    const text = Array.isArray(v) ? v.join(', ') : String(v);
    bullets.push(`${titleCase(k)}: ${text}`);
  });

  if (bullets.length === 0) return <span className="text-gray-500">N/A</span>;

  return (
    <ul className="list-disc pl-5 space-y-1 break-words text-[13px] leading-5">
      {bullets.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  );
}


const AUDITS_SUBDOMAIN = 'audits';

export default function TenantAuditsSubmissions() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        // Resolve audits tenant_id by subdomain
        const { data: tenantData, error: tenantErr } = await supabase
          .from('tenants')
          .select('id')
          .eq('subdomain', AUDITS_SUBDOMAIN)
          .single();
        if (tenantErr) throw tenantErr;
        const tenantId = tenantData?.id;

        // Fetch submissions in the same shape as excel-export.ts exportTenantSubmissions
        const { data, error } = await supabase
          .from('submissions')
          .select(`
            id,
            answers_json,
            submitted_at,
            leads!inner(id, created_at, lead_contacts(name, email, phone)),
            products!inner(slug, name, group)
          `)
          .eq('tenant_id', tenantId)
          .order('submitted_at', { ascending: false });
        if (error) throw error;

        const mapped: Row[] = (data || []).map((s: any) => {
          const contact = s.leads?.lead_contacts?.[0] || {};
          const answers = s.answers_json || {};
          return {
            submission_id: s.id,
            lead_id: s.leads?.id || 'N/A',
            contact_name: contact.name || 'N/A',
            contact_email: contact.email || 'N/A',
            contact_phone: contact.phone || 'N/A',
            product_slug: s.products?.slug || 'N/A',
            product_name: s.products?.name || 'N/A',
            product_group: s.products?.group || 'N/A',
            answers: answers,
            submitted_at: s.submitted_at ? new Date(s.submitted_at).toLocaleString() : 'N/A',
            lead_created_at: s.leads?.created_at ? new Date(s.leads.created_at).toLocaleString() : 'N/A',
          };
        });

        setRows(mapped);
      } catch (e) {
        console.error('Failed to fetch audits submissions:', e);
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      r.contact_name.toLowerCase().includes(q) ||
      r.contact_email.toLowerCase().includes(q) ||
      r.product_name.toLowerCase().includes(q) ||
      r.product_slug.toLowerCase().includes(q)
    );
  }, [rows, search]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Tenant Dashboard - Audits</h1>
        <p className="text-gray-600">All submissions for the Audits tenant (Excel format)</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">Submissions</CardTitle>
          <CardDescription>Read-only list mirroring the Excel export columns</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 mb-4">
            <Label htmlFor="search" className="sr-only">Search</Label>
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input id="search" placeholder="Search by name, email or product" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
            </div>
            <Badge variant="secondary">{filtered.length} results</Badge>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>submission_id</TableHead>
                  <TableHead>lead_id</TableHead>
                  <TableHead>product_slug</TableHead>
                  <TableHead>product_name</TableHead>
                  <TableHead>product_group</TableHead>
                  <TableHead>submitted_at</TableHead>
                  <TableHead>lead_created_at</TableHead>
                  <TableHead>contact_name</TableHead>
                  <TableHead>contact_email</TableHead>
                  <TableHead>contact_phone</TableHead>
                  <TableHead>answers</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin inline-block mr-2" /> Loading submissions...
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                      No submissions found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r) => (
                    <TableRow key={r.submission_id}>
                      <TableCell className="font-mono text-xs">{r.submission_id}</TableCell>
                      <TableCell className="font-mono text-xs">{r.lead_id}</TableCell>
                      <TableCell>{r.product_slug}</TableCell>
                      <TableCell>{r.product_name}</TableCell>
                      <TableCell>{r.product_group}</TableCell>
                      <TableCell>{r.submitted_at}</TableCell>
                      <TableCell>{r.lead_created_at}</TableCell>
                      <TableCell>{r.contact_name}</TableCell>
                      <TableCell>{r.contact_email}</TableCell>
                      <TableCell>{r.contact_phone}</TableCell>
                      <TableCell className="min-w-[420px] max-w-[800px] text-xs leading-5">{renderAnswers(r.answers)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

